import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
	TABLES_AND_STATIC_DOCS_ENABLED,
	useAuthContext,
} from '../../context/auth';
import {
	NamespaceAccountDetails,
	NamespaceClientDetails,
	NamespaceClients,
	NamespaceMigrate,
	NamespaceTenantManagement,
	NamespaceTenants,
	NamespaceUserDetails,
	NamespaceUsers,
} from './namespace';
import { Layout } from './namespace/components';
import { Users } from './tenant';
import {
	AddRole,
	EditRole,
	Integrations,
	ScriptLogs,
	StaticDocuments,
} from './tenant/pages';
import DataAutofillsPanel from './tenant/pages/Integrations/DataAutofillsPanel';
import ExternalRatersPanel from './tenant/pages/Integrations/ExternalRatersPanel';
import WebhooksPanel from './tenant/pages/Integrations/WebhooksPanel';
import ResourceGroups from './tenant/pages/ResourceGroups/ResourceGroups';
import Roles from './tenant/pages/Roles/Roles';
import Tables from './tenant/pages/Tables/Tables';

interface NamespaceAdminAppProps {
	error: boolean;
}

const NamespaceAdminApp = (props: NamespaceAdminAppProps) => {
	const { error } = props;
	useEffect(() => {
		document.title = 'Socotra - Administration v2';
	});
	const authData = useAuthContext();

	const tablesAndStaticDocsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			TABLES_AND_STATIC_DOCS_ENABLED
		];

	return (
		<Routes>
			<Route
				path='admin'
				element={<Layout authData={authData} error={error} />}
			>
				<Route path='users'>
					<Route index element={<NamespaceUsers />} />
					<Route path=':userUuid' element={<NamespaceUserDetails />} />
				</Route>
				<Route path='tenants'>
					<Route index element={<NamespaceTenants />} />
					<Route path=':tenantHostname' element={<NamespaceTenantManagement />}>
						<Route path='users' element={<Users />} />
						<Route path='script-logs' element={<ScriptLogs />} />
						{tablesAndStaticDocsEnabled && (
							<Route path='tables' element={<Tables />} />
						)}
						{tablesAndStaticDocsEnabled && (
							<Route path='static-docs' element={<StaticDocuments />} />
						)}
						<Route path='resource-groups' element={<ResourceGroups />} />
						<Route path='roles'>
							<Route index element={<Roles />} />
							<Route path='edit/:roleName' element={<EditRole />} />
							<Route path='new' element={<AddRole />} />
						</Route>
						<Route path={'integrations'} element={<Integrations />}>
							<Route
								path={'external-raters'}
								element={<ExternalRatersPanel />}
							/>
							<Route path={'data-autofills'} element={<DataAutofillsPanel />} />
							<Route path={'webhooks'} element={<WebhooksPanel />} />
						</Route>
					</Route>
				</Route>
				<Route path='clients'>
					<Route index element={<NamespaceClients />} />
					<Route path=':clientUuid' element={<NamespaceClientDetails />} />
				</Route>
				{authData.ssoConfig.ssoMigrationEnabled && (
					<Route path='migrate' element={<NamespaceMigrate />} />
				)}
				<Route path='account' element={<NamespaceAccountDetails />} />
				<Route path='*' element={<Navigate to='/admin/users' />} />
			</Route>
			<Route path='*' element={<Navigate to='/admin/users' />} />
		</Routes>
	);
};

export default NamespaceAdminApp;
